import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ArrowRight, Bot, Loader2, X } from 'lucide-react';
import { api } from '../api/client';
import type { SessionInfo } from '../api/types';
import { prettyModel } from './NewSessionDialog';
import { pushToast } from '../lib/toastStore';
import './ResetModal.css';
import './CloneDialog.css';
import './AgentSwitchDialog.css';

type SwitchAgent = 'claude' | 'codex' | 'agy';
type ModelCatalog = Awaited<ReturnType<typeof api.models>>;

interface Props {
  session: SessionInfo;
  onClose: () => void;
  onSwitched?: (name: string) => void;
}

const AGENT_LABELS: Record<string, string> = {
  claude: 'Claude',
  codex: 'Codex',
  agy: 'Anti-Gravity',
  gemini: 'Gemini',
};

const FALLBACK_EFFORTS: Record<SwitchAgent, string[]> = {
  claude: ['low', 'medium', 'high', 'xhigh', 'max'],
  codex: [], // fail closed until the live catalog tells us what Codex accepts
  agy: ['low', 'medium', 'high', 'xhigh', 'max'],
};

function labelAgent(agent?: string): string {
  return AGENT_LABELS[(agent || 'claude').toLowerCase()] || agent || 'Claude';
}

/**
 * Manual CLI/LLM handoff for an existing agent (#562).
 *
 * The backend does not convert raw transcripts. It writes a compact HANDOFF.md,
 * keeps the source CLI history on disk, changes the registry type and restarts
 * the same named agent. This dialog deliberately describes that lossy boundary
 * and points users to Clone first when the original must remain runnable.
 */
export function AgentSwitchDialog({ session, onClose, onSwitched }: Props) {
  const currentAgent = (session.agent || 'claude').toLowerCase();
  const initialTarget: SwitchAgent = currentAgent === 'claude' ? 'codex' : 'claude';
  const [targetAgent, setTargetAgent] = useState<SwitchAgent>(initialTarget);
  const [model, setModel] = useState('');
  const [carryContext, setCarryContext] = useState(true);
  const [acknowledged, setAcknowledged] = useState(false);
  const [catalog, setCatalog] = useState<ModelCatalog | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState(false);
  const [running, setRunning] = useState(false);
  const [errorText, setErrorText] = useState('');

  useEffect(() => {
    let cancelled = false;
    setCatalogLoading(true);
    api.models()
      .then((result) => {
        if (cancelled) return;
        setCatalog(result);
        setCatalogError(false);
        setCatalogLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setCatalogError(true);
        setCatalogLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Never inherit a model across providers. The switch endpoint keeps the old
  // registry model when the field is omitted, so every request must send an
  // explicit target-compatible value. Claude/Agy "" means CLI default; Codex
  // uses the live catalog default (currently gpt-5.6-sol), never the
  // `codex-default` placeholder from the list.
  const codexDefault = catalog?.defaults?.codex || '';
  useEffect(() => {
    setModel(targetAgent === 'codex' ? codexDefault : '');
    setErrorText('');
  }, [targetAgent, codexDefault]);

  // Nur CLIs anbieten, die der Katalog dieser Instanz kennt. Auf einer
  // Kundeninstanz ist nicht alles bereitgestellt (Davids Instanz: nur codex) —
  // eine Auswahl, die dort gar nicht startet, ist ein Versprechen, das die
  // Instanz nicht halten kann. Gleiche Regel wie im Neu-Dialog (#157).
  // Fällt der Katalog aus, bleiben alle drei stehen: lieber eine Option zu
  // viel als ein Portal, das nach einem Netzfehler nichts mehr umschalten kann.
  const cliOptions = useMemo<SwitchAgent[]>(() => {
    if (!catalog) return ['claude', 'codex', 'agy'];
    const has = (list?: string[]) => Array.isArray(list) && list.length > 0;
    const avail = (['claude', 'codex', 'agy'] as SwitchAgent[]).filter((a) =>
      has(catalog[a]) || a === currentAgent);
    return avail.length > 0 ? avail : ['claude', 'codex', 'agy'];
  }, [catalog, currentAgent]);

  const modelOptions = useMemo(() => {
    if (!catalog) return [];
    if (targetAgent === 'codex') {
      const real = (catalog.codex || []).filter((m) => m && m !== 'codex-default');
      return [...new Set([codexDefault, ...real].filter(Boolean))];
    }
    if (targetAgent === 'agy') return catalog.agy || [];
    return catalog.claude || [];
  }, [catalog, codexDefault, targetAgent]);

  const effortOptions = useMemo(() => {
    const live = catalog?.efforts?.[targetAgent]?.available || [];
    return live.length > 0 ? live : FALLBACK_EFFORTS[targetAgent];
  }, [catalog, targetAgent]);
  const targetEffort = useMemo(() => {
    const current = session.effort || '';
    if (effortOptions.includes(current)) return current;
    const preferred = catalog?.efforts?.[targetAgent]?.default || 'high';
    return effortOptions.includes(preferred) ? preferred : (effortOptions[0] || '');
  }, [catalog, effortOptions, session.effort, targetAgent]);
  const effortChanges = !!targetEffort && targetEffort !== (session.effort || '');

  const targetReady = targetAgent !== currentAgent
    && (!catalogError || targetAgent !== 'codex')
    && (targetAgent !== 'codex' || !!model)
    && !!targetEffort;

  const submit = async () => {
    if (!targetReady || !acknowledged || running) return;
    setRunning(true);
    setErrorText('');
    try {
      // #565 workaround: /switch-agent ignores effort. Config PATCH already
      // forwards federation-side, so normalise it first and only when needed.
      if (effortChanges) {
        const configResult = await api.sessions.updateConfig(session.name, { effort: targetEffort });
        if (typeof configResult.error === 'string' && configResult.error) {
          throw new Error(configResult.error);
        }
      }

      const result = await api.sessions.switchAgent(
        session.name,
        { agent: targetAgent, model, carry_context: carryContext },
        session.node_url || '',
      );
      const handoffChars = result.handoff?.chars || 0;
      const handoffText = carryContext
        ? result.handoff_written
          ? `Verdichteter Kontext: ${Math.max(1, Math.round(handoffChars / 1024))} KB.`
          : 'Kein Handoff geschrieben (keine übertragbare History oder Handoff-Fehler).'
        : 'Ohne Kontextübergabe gestartet.';
      const wakeWarning = result.wake?.error ? ` Start-Hinweis fehlgeschlagen: ${result.wake.error}` : '';
      pushToast({
        dedupKey: `agent-switch:${session.name}`,
        variant: carryContext && !result.handoff_written ? 'info' : 'success',
        dismiss: 'manual',
        content: `✓ ${session.name}: ${labelAgent(result.from)} → ${labelAgent(result.to)}. ${handoffText}${wakeWarning}`,
      });
      onSwitched?.(session.name);
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unbekannter Fehler';
      setErrorText(
        /failed to fetch|network|cors/i.test(message) && session.node_url
          ? `Remote-Node konnte nicht direkt erreicht werden. ${message} (#565)`
          : message,
      );
      setRunning(false);
    }
  };

  return (
    <div className="reset-modal-backdrop" onClick={running ? undefined : onClose}>
      <div className="reset-modal agent-switch-modal" onClick={(event) => event.stopPropagation()}>
        <header className="reset-modal-header">
          <h2><Bot size={18} /> CLI / LLM wechseln — {session.name}</h2>
          <button className="reset-modal-close" onClick={onClose} disabled={running} aria-label="Schließen">
            <X size={16} />
          </button>
        </header>

        <div className="reset-modal-warning agent-switch-warning">
          <AlertTriangle size={15} />
          <span>
            <strong>Der Agent wird neu gestartet.</strong> Seine Identität und Persona bleiben,
            aber der neue CLI erhält nur einen <strong>verdichteten Handoff</strong>, nicht das
            vollständige Gedächtnis. Die alte History bleibt zwar auf der Platte, wird vom neuen
            CLI aber nicht weitergelesen. Wenn das Original sicher weiterlaufen soll: abbrechen,
            zuerst <strong>Klonen</strong> wählen und danach den Klon wechseln.
          </span>
        </div>

        <div className="agent-switch-route" aria-label={`${labelAgent(currentAgent)} zu ${labelAgent(targetAgent)}`}>
          <span className="agent-switch-node current">{labelAgent(currentAgent)}</span>
          <ArrowRight size={17} />
          <span className="agent-switch-node target">{labelAgent(targetAgent)}</span>
        </div>

        <div className="agent-switch-grid">
          <label className="reset-modal-label agent-switch-field">
            Ziel-CLI
            <select
              className="agent-switch-select"
              value={targetAgent}
              disabled={running}
              onChange={(event) => setTargetAgent(event.target.value as SwitchAgent)}
            >
              {cliOptions.map((agent) => (
                <option key={agent} value={agent} disabled={agent === currentAgent}>
                  {labelAgent(agent)}{agent === currentAgent ? ' (aktuell)' : ''}
                </option>
              ))}
            </select>
          </label>

          <label className="reset-modal-label agent-switch-field">
            Zielmodell
            <select
              className="agent-switch-select"
              value={model}
              disabled={running || catalogLoading || (targetAgent === 'codex' && !codexDefault)}
              onChange={(event) => setModel(event.target.value)}
            >
              {targetAgent !== 'codex' && <option value="">CLI-Default</option>}
              {modelOptions.map((entry) => (
                <option key={entry} value={entry}>
                  {targetAgent === 'codex' && entry === codexDefault
                    ? `Standard (${entry})`
                    : prettyModel(entry)}
                </option>
              ))}
            </select>
            <small>
              {catalogLoading
                ? 'Modellkatalog wird geladen…'
                : targetAgent === 'codex' && !codexDefault
                  ? 'Codex-Default fehlt im Live-Katalog — Wechsel ist sicherheitshalber gesperrt.'
                  : `Effort nach Wechsel: ${targetEffort || 'nicht verfügbar'}${effortChanges ? ` (wird von ${session.effort || 'leer'} normalisiert)` : ''}`}
            </small>
          </label>
        </div>

        {catalogError && (
          <div className="agent-switch-error">
            Modellkatalog nicht erreichbar. Claude/Anti-Gravity können mit CLI-Default wechseln;
            Codex bleibt gesperrt, bis sein Live-Default verifiziert ist.
          </div>
        )}

        <label className="clone-check agent-switch-check">
          <input
            type="checkbox"
            checked={carryContext}
            disabled={running}
            onChange={(event) => setCarryContext(event.target.checked)}
          />
          <span>
            Verdichteten Kontext mitnehmen
            <small>Compact-Summary + jüngste Turns werden in HANDOFF.md geschrieben.</small>
          </span>
        </label>

        {!carryContext && (
          <div className="agent-switch-error">
            Ohne Kontextübergabe startet der Ziel-CLI mit Persona, aber ohne den bisherigen Gesprächsstand.
          </div>
        )}

        <label className="reset-modal-cloud-guard agent-switch-ack">
          <input
            type="checkbox"
            checked={acknowledged}
            disabled={running}
            onChange={(event) => setAcknowledged(event.target.checked)}
          />
          <span>
            <strong>Direkten Wechsel verstanden.</strong> Der Handoff ist eine Verdichtung und
            ersetzt keine vollständige, CLI-übergreifende History.
          </span>
        </label>

        {errorText && <div className="agent-switch-error" role="alert">{errorText}</div>}

        <div className="reset-modal-actions">
          <button className="reset-modal-cancel" onClick={onClose} disabled={running}>
            Abbrechen
          </button>
          <button
            className="reset-modal-submit agent-switch-submit"
            onClick={submit}
            disabled={!targetReady || !acknowledged || running}
          >
            {running ? <><Loader2 size={14} className="clone-spin" /> Wechsel läuft…</> : 'CLI wechseln & neu starten'}
          </button>
        </div>
      </div>
    </div>
  );
}
